import React from "react";

import { useTranslation } from "utils";

import { GoalScore } from "models";

import { Divider, Flex, IconLinkButton, OverflowEllipsisWithTooltip, Tag, Typography } from "components";

import { ScoreCircleChart, ScoreColor } from "pages/app/crop-mix-composer/components/compositionStep/ScoreCircleChart.tsx";

export type MixItem = {
  name: string;
  weightShare: number;
};

export type TopMatchCardCommercialMix = {
  id: string;
  provider: string;
  name: string;
  matchScore: number;
  mixItem: MixItem[];
  goalScores: GoalScore[];
  link: string | null;
};

type TopMatchCardProps = {
  commercialMix: TopMatchCardCommercialMix;
  isPopover?: boolean;
};

export const TopMatchCard: React.FC<TopMatchCardProps> = ({ commercialMix, isPopover }) => {
  const { t } = useTranslation("cropMixComposer");

  const handleContactProvider = () => {
    if (commercialMix.link) {
      window.open(commercialMix.link, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Flex className={`top-match-card ${isPopover ? "popover" : ""}`} data-testid="top-match-card" vertical justify="space-evenly" gap={"2rem"}>
      {/* Header */}
      <Flex className="header" justify="space-between">
        <Flex vertical align="flex-start" flex={1} style={{ maxWidth: "14rem" }}>
          <Typography variant="h6" className="mix-name">
            {commercialMix.name}
          </Typography>
          <Typography variant="paragraph-m" className="provider">
            {commercialMix.provider}
          </Typography>
        </Flex>
        <Flex vertical align="flex-end" gap={"0.25rem"}>
          <Tag className="tag" colour="yellow" text={t("topMatchCard.matchScore", { matchScore: commercialMix.matchScore })} />
          <Typography variant="paragraph-xs">{t("topMatchCard.withYourMix")}</Typography>
        </Flex>
      </Flex>
      {/* Crop Mix */}
      <Flex className="crops-table" vertical>
        <Flex className="header margin-bottom-50" justify="space-between">
          <Typography variant="paragraph-xs">{t("topMatchCard.cropsInMix")}</Typography>
          <Typography variant="paragraph-xs">{t("topMatchCard.weightShare")}</Typography>
        </Flex>
        <Divider />
        <div className="content">
          {commercialMix.mixItem.map((mixItem) => (
            <React.Fragment key={mixItem.name}>
              <Flex justify={"space-between"}>
                <Typography variant="paragraph-m" className="margin-top-50 margin-bottom-50">
                  {mixItem.name}
                </Typography>
                <Typography variant="paragraph-m" bold className="margin-top-50 margin-bottom-50">{`${mixItem.weightShare}%`}</Typography>
              </Flex>
              <Divider />
            </React.Fragment>
          ))}
        </div>
      </Flex>
      {/* Match Scores */}
      <Flex className="match-scores" justify="space-evenly" gap="0.5rem">
        {commercialMix.goalScores.map((goalScore, index) => (
          <Flex key={goalScore.name} align="center" vertical flex="1">
            <ScoreCircleChart
              dataTestId={`score-circle-chart-goalScore${index + 1}`}
              score={goalScore.score}
              color={Object.values(ScoreColor)[index]}
              size={50}
            />
            <Flex align="center">
              <OverflowEllipsisWithTooltip className="goal-label" title={t(`goals.${goalScore.name}`)} verticalOverflow>
                {t(`goals.${goalScore.name}`)}
              </OverflowEllipsisWithTooltip>
            </Flex>
          </Flex>
        ))}
        {commercialMix.goalScores.length === 0 && <div></div>}
      </Flex>
      {/* Footer */}
      {!isPopover && (
        <Flex className="footer" justify="flex-end">
          <IconLinkButton disabled={commercialMix.link === null} iconType="logout" text={t("topMatchCard.contactProvider")} onClick={handleContactProvider} />
        </Flex>
      )}
    </Flex>
  );
};
